import React, { useState, useContext, useEffect } from 'react';
import { PageContext } from '../frontend/pageContext';
import { ClientMenuGroup, ClientSuperMenuItem } from '../types';
import MenuLink from './MenuLink';
import { checkIsOpenedMenu, checkIsRootMenu } from './utils';

interface Props {
  menuGroup: ClientMenuGroup<ClientSuperMenuItem>;
  index: number;
  singleDocLeaf?: boolean;
  menuRoot: string | null;
}

const MenuGroup = ({ menuGroup, index, singleDocLeaf, menuRoot }: Props) => {
  const { pathname } = useContext(PageContext);
  const [clientExpanded, setClientExpanded] = useState(
    menuGroup.serverExpanded,
  );
  const [indicatorClass, setIndicatorClass] = useState('menu-indicator');

  useEffect(() => {
    if (singleDocLeaf) {
      return;
    }
    const isRootMenu = checkIsRootMenu(menuGroup, menuRoot);
    const isOpenedMenu = checkIsOpenedMenu(menuGroup, pathname);
    setIndicatorClass(
      isRootMenu
        ? 'menu-root-indicator'
        : isOpenedMenu
          ? 'menu-current-indicator'
          : 'menu-indicator',
    );
  }, [menuGroup, menuRoot, pathname, singleDocLeaf]);

  let icon;

  if (!singleDocLeaf) {
    menuGroup.children.sort((x, y) => {
      if (x.type === 'menuLink' && y.type === 'menuLink') {
        if (x.url === 'index' && y.url === 'index') return 0;
        if (x.url === 'index') return -1;
        if (y.url === 'index') return 1;
      }
      return 0;
    });
  }

  const getMenuClass = () => {
    let menuClass = '';
    if (menuGroup.children) {
      menuClass += 'has-menu ';
    }
    if (menuGroup.active) {
      menuClass += 'is-active ';
    }
    if (menuGroup.children && clientExpanded) {
      menuClass += 'is-expanded';
    }
    return menuClass;
  };

  if (menuGroup.children && !clientExpanded) {
    icon = <div className="right-caret"></div>;
  } else if (menuGroup.children && clientExpanded) {
    icon = <div className="down-caret"></div>;
  } else {
    icon = undefined;
  }

  const menuItems = (
    <>
      {clientExpanded &&
        menuGroup.children?.map((menuItem, i) => {
          if (
            menuItem.type === 'menuLink' ||
            menuItem.type === 'externalMenuLink'
          ) {
            return (
              <MenuLink
                menuLink={menuItem}
                index={i}
                key={i}
                menuRoot={menuRoot}
              />
            );
          } else if (menuItem.type === 'menuGroup') {
            return (
              <MenuGroup
                menuGroup={menuItem}
                index={i}
                key={i}
                menuRoot={menuRoot}
              />
            );
          } else {
            return <></>;
          }
        })}
    </>
  );

  if (singleDocLeaf) {
    return menuItems;
  }

  return (
    <ul key={`ul-${index}`}>
      <li key={`${index}-${String(menuGroup.title)}`}>
        <a
          href={menuGroup.isLeafRoot ? menuGroup.path : ''}
          data-test-id="menu-group"
          key={`a-${index}`}
          className={getMenuClass()}
          onClick={(event) => {
            if (
              menuGroup.active ||
              !menuGroup.isLeafRoot ||
              getMenuClass().split(' ').includes('is-expanded')
            ) {
              setClientExpanded(!clientExpanded);
              event.preventDefault();
            } else {
              setClientExpanded(true);
              event.preventDefault();
            }
          }}
        >
          {menuGroup.title}
          {icon}
          <div key={`div-${index}`} className={indicatorClass}></div>
        </a>
        <ul>{menuItems}</ul>
      </li>
    </ul>
  );
};

export default MenuGroup;
